import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { filterSidebarNavByAccess } from "@/config/sidebar-nav-access.js";
import { filterSidebarNavByUrls, sidebarSubItemMatchesPath } from "@/config/sidebar-nav.utils.js";
import {
  buildModuleNavigation,
  resolveWorkspaceForPath,
} from "@/config/module-navigation.js";
import { createNavData } from "@/config/sidebar-nav.config.js";
import { usePartyAssignmentFlags } from "@/hooks/usePartyAssignmentFlags.js";
import { usePermission } from "@/hooks/usePermission";
import {
  ensureSidebarLabelNamespaces,
  hasSidebarLabelNamespaces,
  subscribeSidebarLabelNamespaces,
} from "@/lib/i18n-sidebar-label-namespaces.js";
import { ticketConfigIntegration } from "@/lib/api/client.js";

import { prefetchNamespacesForPath } from "@/lib/i18n-route-namespaces.js";
import {
  resolveSidebarWorkspacePreferences,
  useSidebarPreferencesStore,
} from "@/store/sidebar-preferences.store.js";

// Prevent consumers from rendering translation keys while sidebar label namespaces load.
const EMPTY_NAV_DATA = {
  sections: [],
  navMain: [],
  navSecondary: [],
};

function normalizePathname(pathname) {
  if (typeof pathname !== "string" || pathname.length === 0) return "";
  const clean = pathname.split("?")[0].split("#")[0] || "/";
  return clean === "/" ? clean : clean.replace(/\/$/, "");
}

function pageMatchesPath(pathname, page) {
  const path = normalizePathname(pathname);
  if (!path || !page?.url) return false;
  return sidebarSubItemMatchesPath(path, {
    ...page,
    url: normalizePathname(page.url),
  });
}

function workspaceHasPath(pathname, workspacePages, allPages, quickAccessUrls) {
  return (workspacePages || []).some((page) => pageMatchesPath(pathname, page))
    || (allPages || []).some((page) => (
      quickAccessUrls?.has(page.url) && pageMatchesPath(pathname, page)
    ));
}

export function useSidebarNavigation(
  preferenceScopeKey,
  { preferencesReady = false } = {},
) {
  const { t, i18n } = useTranslation(["sidebar", "breadcrumb"]);
  const location = useLocation();
  const navigate = useNavigate();
  const [labelsReady, setLabelsReady] = React.useState(() => (
    hasSidebarLabelNamespaces(i18n.resolvedLanguage || i18n.language)
  ));
  const [labelsError, setLabelsError] = React.useState(false);
  const { canView, can, licensedModules } = usePermission();
  const partyAssignment = usePartyAssignmentFlags();
  const canViewTicketConfig = canView("ticketConfig");
  const [ticketFeatureFlags, setTicketFeatureFlags] = React.useState({});

  React.useEffect(() => {
    if (!labelsReady || !canViewTicketConfig) {
      setTicketFeatureFlags({});
      return undefined;
    }

    let cancelled = false;
    setTicketFeatureFlags({});
    ticketConfigIntegration.fetchConfig({ silent: true }).then((response) => {
      if (!cancelled) {
        setTicketFeatureFlags(response?.data?.features || {});
      }
    });

    return () => {
      cancelled = true;
    };
  }, [canViewTicketConfig, labelsReady]);

  const storedWorkspacePreferences = useSidebarPreferencesStore((state) => (
    preferenceScopeKey ? state.workspacePreferencesByScope?.[preferenceScopeKey] : undefined
  ));
  const setActiveWorkspace = useSidebarPreferencesStore((state) => state.setActiveWorkspace);
  const toggleFavoritePage = useSidebarPreferencesStore((state) => state.toggleFavoritePage);
  const { activeWorkspace: selectedWorkspace, favoritePageUrls, customWorkspaces } = React.useMemo(
    () => resolveSidebarWorkspacePreferences(storedWorkspacePreferences),
    [storedWorkspacePreferences],
  );

  const syncLabelNsReady = React.useCallback((loadError = false) => {
    const ready = hasSidebarLabelNamespaces(i18n.resolvedLanguage || i18n.language);
    setLabelsReady(ready);
    if (loadError || !ready) setLabelsError(true);
    if (ready) setLabelsError(false);
    return ready;
  }, [i18n]);

  const retryLabels = React.useCallback(() => {
    setLabelsError(false);
    return ensureSidebarLabelNamespaces()
      .then(() => syncLabelNsReady())
      .catch(() => {
        syncLabelNsReady(true);
        return false;
      });
  }, [syncLabelNsReady]);

  React.useEffect(() => {
    setLabelsError(false);
    setLabelsReady(hasSidebarLabelNamespaces(i18n.resolvedLanguage || i18n.language));
    let cancelled = false;
    ensureSidebarLabelNamespaces()
      .then(() => {
        if (!cancelled) syncLabelNsReady();
      })
      .catch(() => {
        if (!cancelled) syncLabelNsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.language, i18n.resolvedLanguage, preferenceScopeKey, syncLabelNsReady]);

  React.useEffect(
    () => subscribeSidebarLabelNamespaces(() => syncLabelNsReady()),
    [syncLabelNsReady],
  );

  React.useEffect(() => {
    const onLoaded = () => syncLabelNsReady();
    i18n.on("loaded", onLoaded);
    return () => i18n.off("loaded", onLoaded);
  }, [i18n, syncLabelNsReady]);

  const rawData = React.useMemo(
    () => {
      if (!labelsReady) return EMPTY_NAV_DATA;

      return createNavData(
        (key, options) => t(key, { ns: "sidebar", ...options }),
        {
          tNav: (key, options) => i18n.t(key, {
            ns: "definitionsNav",
            defaultValue: options?.defaultValue ?? key,
            ...options,
          }),
          tBreadcrumb: (key, options) => t(key, { ns: "breadcrumb", ...options }),
          tMatrix: (key, options) => i18n.t(key, {
            ns: "matrixConfiguration",
            defaultValue: options?.defaultValue ?? key,
            ...options,
          }),
        },
      );
    },
    [i18n, labelsReady, t],
  );

  const allData = React.useMemo(
    () => filterSidebarNavByAccess(rawData, {
      canView,
      can,
      partyAssignment,
      featureFlags: ticketFeatureFlags,
    }),
    [can, canView, partyAssignment, rawData, ticketFeatureFlags],
  );

  const { allPages, moduleWorkspaces, quickAccessUrls } = React.useMemo(
    () => buildModuleNavigation({ allData, licensedModules }),
    [allData, licensedModules],
  );

  const moduleQuery = React.useMemo(() => {
    if (location.pathname !== "/home") return null;
    return new URLSearchParams(location.search).get("module");
  }, [location.pathname, location.search]);
  const hasModuleQuery = location.pathname === "/home"
    && new URLSearchParams(location.search).has("module");
  const requestedWorkspace = hasModuleQuery
    ? (moduleQuery === "all" || moduleWorkspaces.some(({ key }) => key === moduleQuery)
      ? moduleQuery
      : "all")
    : selectedWorkspace;
  const activeWorkspace = React.useMemo(
    () => resolveWorkspaceForPath({
      pathname: location.pathname,
      requestedWorkspace,
      moduleWorkspaces,
      customWorkspaces,
      allPages,
      quickAccessUrls,
    }),
    [allPages, customWorkspaces, location.pathname, moduleWorkspaces, quickAccessUrls, requestedWorkspace],
  );

  const activeModule = moduleWorkspaces.find(({ key }) => key === activeWorkspace);
  const activeCustomWorkspace = customWorkspaces.find(({ id }) => id === activeWorkspace);
  const data = React.useMemo(() => {
    if (activeModule) return activeModule.data;
    if (activeCustomWorkspace) return filterSidebarNavByUrls(allData, activeCustomWorkspace.pageUrls);
    return allData;
  }, [activeCustomWorkspace, activeModule, allData]);

  const favoritePages = React.useMemo(() => {
    const pagesByUrl = new Map(allPages.map((page) => [page.url, page]));
    return favoritePageUrls.map((url) => pagesByUrl.get(url)).filter(Boolean);
  }, [allPages, favoritePageUrls]);
  const favoriteUrls = React.useMemo(() => new Set(favoritePages.map((page) => page.url)), [favoritePages]);

  React.useEffect(() => {
    if (!preferencesReady || !labelsReady || !preferenceScopeKey) return;
    if (activeWorkspace === selectedWorkspace) return;
    setActiveWorkspace(preferenceScopeKey, activeWorkspace);
  }, [activeWorkspace, labelsReady, preferenceScopeKey, preferencesReady, selectedWorkspace, setActiveWorkspace]);

  const isSelectableWorkspace = React.useCallback((workspaceKey) => {
    if (workspaceKey === "all" || moduleWorkspaces.some(({ key }) => key === workspaceKey)) return true;
    const currentPreferences = resolveSidebarWorkspacePreferences(
      useSidebarPreferencesStore.getState().workspacePreferencesByScope?.[preferenceScopeKey],
    );
    return currentPreferences.customWorkspaces.some(({ id }) => id === workspaceKey);
  }, [moduleWorkspaces, preferenceScopeKey]);

  const activateWorkspace = React.useCallback((workspaceKey) => {
    if (!preferenceScopeKey || !isSelectableWorkspace(workspaceKey)) return false;
    setActiveWorkspace(preferenceScopeKey, workspaceKey);
    return true;
  }, [isSelectableWorkspace, preferenceScopeKey, setActiveWorkspace]);

  const openModule = React.useCallback((moduleKey) => {
    const module = moduleWorkspaces.find(({ key }) => key === moduleKey);
    if (!module || !activateWorkspace(moduleKey)) return false;
    if (quickAccessUrls.has("/home")) {
      navigate(`/home?module=${encodeURIComponent(moduleKey)}`);
    } else if (module.featuredPages[0]?.url) {
      void prefetchNamespacesForPath(module.featuredPages[0].url);
      navigate(module.featuredPages[0].url);
    }
    return true;
  }, [activateWorkspace, moduleWorkspaces, navigate, quickAccessUrls]);

  const selectWorkspace = React.useCallback((workspaceKey) => {
    if (!isSelectableWorkspace(workspaceKey)) return false;
    if (workspaceKey === "all") {
      activateWorkspace("all");
      if (location.pathname === "/home" && hasModuleQuery) navigate("/home");
      return true;
    }

    const module = moduleWorkspaces.find(({ key }) => key === workspaceKey);
    if (module) {
      const routeIsCompatible = location.pathname !== "/home"
        && workspaceHasPath(location.pathname, module.pages, allPages, quickAccessUrls);
      if (routeIsCompatible) {
        activateWorkspace(workspaceKey);
        return true;
      }
      return openModule(workspaceKey);
    }

    const currentPreferences = resolveSidebarWorkspacePreferences(
      useSidebarPreferencesStore.getState().workspacePreferencesByScope?.[preferenceScopeKey],
    );
    const custom = currentPreferences.customWorkspaces.find(({ id }) => id === workspaceKey);
    if (!custom) return false;
    activateWorkspace(workspaceKey);
    if (location.pathname === "/home") {
      if (hasModuleQuery) navigate("/home");
      return true;
    }
    if (workspaceHasPath(location.pathname, allPages.filter((page) => custom.pageUrls.includes(page.url)), allPages, quickAccessUrls)) {
      return true;
    }
    const firstPage = allPages.find((page) => custom.pageUrls.includes(page.url));
    if (firstPage?.url) {
      void prefetchNamespacesForPath(firstPage.url);
      navigate(firstPage.url);
    }
    return true;
  }, [activateWorkspace, allPages, hasModuleQuery, isSelectableWorkspace, location.pathname, moduleWorkspaces, navigate, openModule, preferenceScopeKey, quickAccessUrls]);

  const toggleFavorite = React.useCallback((url) => {
    if (!preferenceScopeKey || !allPages.some((page) => page.url === url)) return;
    toggleFavoritePage(preferenceScopeKey, url);
  }, [allPages, preferenceScopeKey, toggleFavoritePage]);

  return {
    data,
    allData,
    allPages,
    moduleWorkspaces,
    quickAccessUrls,
    availableModules: moduleWorkspaces,
    activeWorkspace,
    customWorkspaces,
    favoritePages,
    favoriteUrls,
    labelsReady,
    labelsError,
    retryLabels,
    preferenceScopeKey,
    activateWorkspace,
    openModule,
    selectWorkspace,
    toggleFavoritePage: toggleFavorite,
    ensureLabelNamespaces: ensureSidebarLabelNamespaces,
    requestedModuleKey: moduleQuery,
  };
}
